/**
 * OrderAssetHub — the "Orders" view inside the Social Library.
 * One card per delivered Social Pro order: Sneak Peek + reels (+ NarraTour / PresenTour), AI captions per asset,
 * grouped download, and "pull in a finished tour" by WMS order id.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Film, FolderDown, Loader2, Sparkles, Copy, Check, Save, ChevronDown, Plus, Inbox, Captions, RefreshCw, Hash, FileText,
} from 'lucide-react';
import { TYPE_META, shortBranch, formatAssetDate } from '@/SocialManagerPortal';
import '@/OrderAssetHub.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLATFORMS = [
  { key: 'instagram', label: 'Instagram' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'tiktok', label: 'TikTok' },
];
const TOURS = [{ key: 'narratour', label: 'NarraTour' }, { key: 'presentour', label: 'PresenTour' }];
const SCRIPT_LABEL = { Delivered: 'Script delivered', delivered: 'Script delivered', completed: 'Script delivered' };

const copyText = (text, what = 'Caption') => navigator.clipboard?.writeText(text).then(() => toast.success(`${what} copied`)).catch(() => toast.error('Copy failed'));

function CaptionPanel({ asset, onReady }) {
  const [data, setData] = useState(null);
  const [platform, setPlatform] = useState('instagram');
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    axios.get(`${API}/asset-hub/captions/${asset.id}`).then((r) => setData(r.data)).catch(() => setData({ captions: {}, hashtags: [], ready: false }));
  }, [asset.id]);

  const generate = () => {
    setBusy(true);
    axios.post(`${API}/asset-hub/captions/${asset.id}/generate`)
      .then((r) => { setData(r.data); setDirty(false); onReady?.(asset.id, true); toast.success('Captions written — tweak anything you like'); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Caption writer failed'))
      .finally(() => setBusy(false));
  };
  const save = () => {
    setBusy(true);
    axios.put(`${API}/asset-hub/captions/${asset.id}`, { captions: data.captions, hashtags: data.hashtags })
      .then((r) => { setData(r.data); setDirty(false); onReady?.(asset.id, r.data.ready); toast.success('Saved for the whole team'); })
      .catch(() => toast.error('Could not save'))
      .finally(() => setBusy(false));
  };
  const copy = (key, text) => { copyText(text); setCopied(key); setTimeout(() => setCopied(''), 1400); };

  if (!data) return <div className="oah-captions oah-captions-loading" data-testid={`oah-captions-${asset.id}`}><Loader2 size={15} className="oah-spin" /> Loading captions…</div>;
  const text = data.captions?.[platform] || '';
  const tags = (data.hashtags || []).join(' ');

  return (
    <div className="oah-captions" data-testid={`oah-captions-${asset.id}`}>
      <div className="oah-captions-head">
        <div className="oah-platforms" role="tablist">
          {PLATFORMS.map((p) => (
            <button key={p.key} type="button" role="tab" aria-selected={platform === p.key} className={`oah-platform ${platform === p.key ? 'active' : ''} ${data.captions?.[p.key] ? 'has' : ''}`} onClick={() => setPlatform(p.key)} data-testid={`oah-platform-${asset.id}-${p.key}`}>{p.label}</button>
          ))}
        </div>
        <button type="button" className="oah-btn oah-btn-ai" onClick={generate} disabled={busy} data-testid={`oah-generate-${asset.id}`}>
          {busy ? <Loader2 size={14} className="oah-spin" /> : data.ready ? <RefreshCw size={14} /> : <Sparkles size={14} />}
          {data.ready ? 'Rewrite with AI' : 'Write captions with AI'}
        </button>
      </div>
      {!data.ready && !busy && (
        <p className="oah-captions-empty" data-testid={`oah-captions-empty-${asset.id}`}>No captions yet — let AI draft Instagram, Facebook, LinkedIn and TikTok versions from the script and property, then edit freely.</p>
      )}
      <textarea
        className="oah-caption-text"
        value={text}
        placeholder={`Write the ${PLATFORMS.find((p) => p.key === platform)?.label} caption…`}
        onChange={(e) => { setData((d) => ({ ...d, captions: { ...d.captions, [platform]: e.target.value } })); setDirty(true); }}
        rows={text.length > 320 ? 8 : 5}
        data-testid={`oah-caption-text-${asset.id}`}
      />
      <div className="oah-hashtags">
        <Hash size={13} />
        <input
          value={tags}
          placeholder="#hashtags separated by spaces"
          onChange={(e) => { setData((d) => ({ ...d, hashtags: e.target.value.split(/\s+/).filter(Boolean) })); setDirty(true); }}
          data-testid={`oah-hashtags-${asset.id}`}
        />
      </div>
      <div className="oah-captions-actions">
        <button type="button" className="oah-btn" onClick={() => copy('cap', text)} disabled={!text} data-testid={`oah-copy-caption-${asset.id}`}>{copied === 'cap' ? <Check size={14} /> : <Copy size={14} />} Copy caption</button>
        <button type="button" className="oah-btn" onClick={() => copy('both', `${text}\n\n${tags}`.trim())} disabled={!text} data-testid={`oah-copy-with-tags-${asset.id}`}>{copied === 'both' ? <Check size={14} /> : <Copy size={14} />} Copy with hashtags</button>
        <span className="oah-flex" />
        {data.updated_at && !dirty && <small className="oah-saved-note">Saved {formatAssetDate(data.updated_at)}{data.edited_by_name ? ` · ${data.edited_by_name}` : ''}</small>}
        <button type="button" className={`oah-btn oah-btn-save ${dirty ? 'is-dirty' : ''}`} onClick={save} disabled={busy || !dirty} data-testid={`oah-save-captions-${asset.id}`}><Save size={14} /> {dirty ? 'Save changes' : 'Saved'}</button>
      </div>
    </div>
  );
}

function PullTourForm({ order, onDone, onCancel }) {
  const [wmsId, setWmsId] = useState('');
  const [type, setType] = useState('narratour');
  const [busy, setBusy] = useState(false);
  const submit = (e) => {
    e.preventDefault();
    if (!/^\d+$/.test(wmsId.trim())) { toast.error('Enter the WMS order number of the finished tour'); return; }
    setBusy(true);
    axios.post(`${API}/asset-hub/pull-tour`, { wms_order_id: Number(wmsId), asset_type: type, attach_to_job_id: order?.wms_job_id || null, branch_id: order?.branch_id || null })
      .then((r) => { toast.success(`${TOURS.find((t) => t.key === type).label} pulled in from WMS #${wmsId}`); onDone(r.data); })
      .catch((err) => toast.error(err.response?.data?.detail || 'Could not pull that tour in'))
      .finally(() => setBusy(false));
  };
  return (
    <form className="oah-pull" onSubmit={submit} data-testid={`oah-pull-form-${order?.group_key ? order.group_key.replace(/[^a-z0-9]/gi, '-') : 'new'}`}>
      <select value={type} onChange={(e) => setType(e.target.value)} data-testid="oah-pull-type">
        {TOURS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
      </select>
      <input value={wmsId} onChange={(e) => setWmsId(e.target.value)} inputMode="numeric" placeholder="WMS order # of the finished tour" data-testid="oah-pull-id" />
      <button type="submit" className="oah-btn oah-btn-primary" disabled={busy} data-testid="oah-pull-submit">{busy ? <Loader2 size={14} className="oah-spin" /> : <Plus size={14} />} Pull in</button>
      {onCancel && <button type="button" className="oah-btn" onClick={onCancel} data-testid="oah-pull-cancel">Cancel</button>}
    </form>
  );
}

function OrderCard({ order, canPull, onOpenAsset, onGroupDownload, downloading, onChanged, focused }) {
  const [openCaption, setOpenCaption] = useState(null);
  const [pulling, setPulling] = useState(false);
  const [ready, setReady] = useState(() => Object.fromEntries(order.assets.map((a) => [a.id, a.caption_ready])));
  const ref = useRef(null);
  useEffect(() => { if (focused && ref.current) ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, [focused]);
  const key = order.group_key.replace(/[^a-z0-9]/gi, '-');
  const readyCount = Object.values(ready).filter(Boolean).length;
  const reels = order.assets.filter((a) => !TOURS.some((t) => t.key === a.asset_type));
  const tours = order.assets.filter((a) => TOURS.some((t) => t.key === a.asset_type));

  return (
    <article ref={ref} className={`oah-order ${focused ? 'is-focused' : ''}`} data-testid={`oah-order-${key}`}>
      <header className="oah-order-head">
        <div className="oah-order-title">
          <h3>{order.title}</h3>
          <div className="oah-order-meta">
            {order.wms_job_id && <span className="smp-wms-pill">WMS #{order.wms_job_id}</span>}
            <span>{(order.branch_names && order.branch_names.length > 1) ? order.branch_names.map(shortBranch).join(', ') : shortBranch(order.branch_name)}</span>
            <span className="oah-dot">•</span>
            <span>{order.assets.length} asset{order.assets.length === 1 ? '' : 's'}</span>
            <span className="oah-dot">•</span>
            <span>{formatAssetDate(order.latest)}</span>
            {order.script?.status && <><span className="oah-dot">•</span><span className="oah-script-pill" data-testid={`oah-script-${key}`}>{SCRIPT_LABEL[order.script.status] || `Script ${order.script.status}`}</span></>}
          </div>
        </div>
        <div className="oah-order-actions">
          <span className={`oah-cap-count ${readyCount === order.assets.length ? 'all' : ''}`} data-testid={`oah-caption-count-${key}`}><Captions size={13} /> {readyCount}/{order.assets.length} captioned</span>
          <button type="button" className={`smp-group-dl ${downloading ? 'downloading' : ''}`} onClick={() => onGroupDownload(order)} disabled={downloading} data-testid={`oah-download-${key}`}>
            {downloading ? <><Loader2 size={15} className="oah-spin" /> Zipping…</> : <><FolderDown size={15} /> Download all{readyCount ? ' + captions' : ''}</>}
          </button>
        </div>
      </header>

      <div className="oah-assets" data-testid={`oah-assets-${key}`}>
        {reels.map((a) => {
          const meta = TYPE_META[a.asset_type] || { label: a.asset_type, color: '#94a3b8' };
          const isOpen = openCaption === a.id;
          return (
            <div key={a.id} className={`oah-asset ${isOpen ? 'is-open' : ''}`} data-testid={`oah-asset-${a.id}`}>
              <button type="button" className="oah-asset-thumb" onClick={() => onOpenAsset(a)} title="Preview & download" data-testid={`oah-asset-preview-${a.id}`}>
                {a.thumb_url ? <img src={a.thumb_url} alt="" loading="lazy" /> : <Film size={20} />}
                <span className="smp-type-badge" style={{ '--badge-color': meta.color }}>{meta.label}</span>
              </button>
              <button type="button" className={`oah-asset-caption-btn ${ready[a.id] ? 'has' : ''}`} onClick={() => setOpenCaption(isOpen ? null : a.id)} data-testid={`oah-caption-toggle-${a.id}`}>
                {ready[a.id] ? <Check size={12} /> : <FileText size={12} />} {ready[a.id] ? 'Captions' : 'Add captions'} <ChevronDown size={12} className={isOpen ? 'is-open' : ''} />
              </button>
            </div>
          );
        })}
      </div>

      <div className="oah-tours" data-testid={`oah-tours-${key}`}>
        {TOURS.map((t) => {
          const have = tours.filter((a) => a.asset_type === t.key);
          const meta = TYPE_META[t.key];
          return (
            <div key={t.key} className={`oah-tour ${have.length ? 'has' : ''}`} style={{ '--tour-color': meta.color }} data-testid={`oah-tour-${key}-${t.key}`}>
              <span className="oah-tour-label">{t.label}</span>
              {have.length ? have.map((a) => (
                <span key={a.id} className="oah-tour-item">
                  <button type="button" className="oah-link" onClick={() => onOpenAsset(a)} data-testid={`oah-tour-open-${a.id}`}><Film size={12} /> Preview &amp; download</button>
                  <button type="button" className="oah-link" onClick={() => setOpenCaption(openCaption === a.id ? null : a.id)} data-testid={`oah-caption-toggle-${a.id}`}><FileText size={12} /> {ready[a.id] ? 'Captions' : 'Add captions'}</button>
                </span>
              )) : <span className="oah-tour-none">Not available yet</span>}
            </div>
          );
        })}
        {canPull && !pulling && (
          <button type="button" className="oah-btn oah-btn-ghost" onClick={() => setPulling(true)} data-testid={`oah-pull-open-${key}`}><Plus size={14} /> Pull in a finished tour from WMS</button>
        )}
      </div>
      {pulling && <PullTourForm order={order} onDone={() => { setPulling(false); onChanged(); }} onCancel={() => setPulling(false)} />}

      {openCaption && order.assets.find((a) => a.id === openCaption) && (
        <CaptionPanel asset={order.assets.find((a) => a.id === openCaption)} onReady={(id, v) => setReady((r) => ({ ...r, [id]: v }))} />
      )}
    </article>
  );
}

export default function OrderAssetHub({ branchId, search, onOpenAsset, onGroupDownload, groupDl, focusJobId, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewPull, setShowNewPull] = useState(false);

  const load = () => {
    const params = {};
    if (branchId && branchId !== 'all') params.branch_id = branchId;
    if (search?.trim()) params.q = search.trim();
    return axios.get(`${API}/asset-hub/orders`, { params }).then((r) => setData(r.data)).catch((e) => { toast.error(e.response?.data?.detail || 'Could not load orders'); setData({ orders: [] }); }).finally(() => setLoading(false));
  };
  useEffect(() => { setLoading(true); load(); }, [branchId, search, refreshKey]); // eslint-disable-line

  const orders = useMemo(() => data?.orders || [], [data]);
  const focusedKey = useMemo(() => (focusJobId ? orders.find((o) => String(o.wms_job_id) === String(focusJobId))?.group_key : null), [orders, focusJobId]);

  if (loading) return <div className="oah-loading" data-testid="oah-loading"><Loader2 size={18} className="oah-spin" /> Loading your orders…</div>;

  return (
    <section className="oah" data-testid="order-asset-hub">
      <div className="oah-toolbar">
        <p className="oah-intro">Every delivered Social Pro order in one place — reels, Sneak Peek, tours and ready-to-post captions.</p>
        {data?.can_pull_tours && !showNewPull && (
          <button type="button" className="oah-btn oah-btn-ghost" onClick={() => setShowNewPull(true)} data-testid="oah-pull-new-open"><Plus size={14} /> Pull in a tour as its own order</button>
        )}
      </div>
      {showNewPull && <PullTourForm order={null} onDone={() => { setShowNewPull(false); load(); }} onCancel={() => setShowNewPull(false)} />}

      {orders.length === 0 ? (
        <div className="smp-empty" data-testid="oah-empty"><Inbox size={40} /><h3>No orders with assets yet</h3><p>As soon as the editors deliver a Sneak Peek or reel for an order it shows up here with its captions.</p></div>
      ) : (
        <div className="oah-list">
          {orders.map((o) => (
            <OrderCard key={o.group_key} order={o} canPull={!!data?.can_pull_tours} onOpenAsset={onOpenAsset} onGroupDownload={onGroupDownload} downloading={groupDl === o.group_key} onChanged={load} focused={focusedKey === o.group_key} />
          ))}
        </div>
      )}
    </section>
  );
}
